import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ShieldCheck, ScanSearch, Loader2, ChevronDown, BookMarked } from 'lucide-react';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Edit-mode banner: NAVI's whole-course fact-check results and the "Approve for learners" gate.
export function CourseReviewBanner({ courseId, token, review, sections = [], quiz = [], quizFlags = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const status = review?.status;
  if (!status || status === 'reviewed') {
    return status === 'reviewed' ? (
      <div className="mb-6 flex items-center gap-2 text-xs text-teal-300" data-testid="course-review-approved"><ShieldCheck className="h-4 w-4" />Content approved for learners by {review.reviewed_by}</div>
    ) : null;
  }
  const flagged = sections.map((s, i) => ({ i, title: s.title, flags: s.fact_flags || [] })).filter((s) => s.flags.length);
  const total = flagged.reduce((n, s) => n + s.flags.length, 0) + quizFlags.length;
  const headers = { Authorization: `Bearer ${token}` };

  const call = async (kind) => {
    setBusy(kind);
    try {
      await axios.post(`${API}/ai/course/${courseId}/content-review${kind === 'recheck' ? '/recheck' : ''}`, {}, { headers });
      toast.success(kind === 'recheck' ? 'NAVI is re-checking the course. Refresh in a minute' : 'Course approved: learners can now open it');
      onChange(kind === 'recheck' ? { status: 'checking' } : { status: 'reviewed', reviewed_by: 'you' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mb-8 rounded-2xl border border-amber-400/40 bg-amber-400/[0.07] p-4" data-testid="course-review-banner">
      <div className="flex flex-wrap items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <p className="text-white font-semibold">{status === 'checking' || status === 'generating' ? 'NAVI is fact-checking this course…' : `Awaiting review: hidden from learners · ${total} item${total === 1 ? '' : 's'} flagged`}</p>
          <p className="text-xs text-zinc-400">Check flagged statements against your sources, edit the text, then approve.</p>
        </div>
        {total > 0 && <button onClick={() => setOpen(!open)} className="text-xs text-amber-200 inline-flex items-center gap-1" data-testid="course-review-toggle">{open ? 'Hide' : 'Show'} flags<ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} /></button>}
        <button onClick={() => call('recheck')} disabled={!!busy} className="text-xs px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-200 hover:bg-zinc-800 inline-flex items-center gap-1.5" data-testid="course-review-recheck-btn">
          {busy === 'recheck' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5" />}Re-check
        </button>
        <button onClick={() => call('approve')} disabled={!!busy || status !== 'pending'} className="text-xs px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-500 text-white inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="course-review-approve-btn">
          {busy === 'approve' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}Approve for learners
        </button>
      </div>
      {open && (
        <div className="mt-4 space-y-3 max-h-80 overflow-y-auto pr-1" data-testid="course-review-flags">
          {flagged.map((s) => (
            <div key={s.i}>
              <p className="text-xs font-semibold text-zinc-300">Section {s.i + 1}: {(s.title || '').replace(/^Section\s+\d+\s*[:.-]\s*/i, '')}</p>
              {s.flags.map((f, k) => <FlagRow key={k} f={f} />)}
            </div>
          ))}
          {quizFlags.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-300">Quiz</p>
              {quizFlags.map((f, k) => <FlagRow key={k} f={{ quote: `Q${f.index + 1}: ${quiz[f.index]?.question || ''}`, reason: f.reason }} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FlagRow = ({ f }) => (
  <div className="mt-1.5 rounded-lg border-l-2 border-amber-400 bg-zinc-900/70 px-3 py-2">
    {f.quote && <p className="text-sm text-amber-100">“{f.quote}”</p>}
    <p className="text-[11px] text-amber-300/90 mt-0.5">{f.reason}</p>
  </div>
);

// Section footer: which trusted sources NAVI used (visible to learners and admins).
export const SectionSources = ({ sources }) => sources?.length ? (
  <div className="mb-6 flex flex-wrap items-center gap-2 text-xs text-zinc-400" data-testid="section-sources">
    <BookMarked className="h-3.5 w-3.5 text-navigate-gold" />Sources:
    {sources.map((s) => <span key={s.doc_id} className="px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-200">{s.title}</span>)}
  </div>
) : null;

// Edit-mode: flags for this section, shown above the lesson text.
export const SectionFactFlags = ({ flags }) => flags?.length ? (
  <div className="mb-6 rounded-xl border border-amber-400/40 bg-amber-400/[0.06] p-3" data-testid="section-fact-flags">
    <p className="text-xs font-semibold text-amber-300 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />NAVI couldn't verify {flags.length} statement{flags.length === 1 ? '' : 's'} in this section. Please check:</p>
    {flags.map((f, k) => <FlagRow key={k} f={f} />)}
  </div>
) : null;
